/**
 * Where this build looks for the daemon, and how somebody changes it.
 *
 * Rendered only where the address is a real question — `daemonAddress` is
 * `null` in a browser, because the page is served by the thing it talks to and
 * a field offering to change that could only break it. Absent rather than
 * disabled, which is the rule the offline pane and the titlebar setting already
 * follow.
 *
 * # Why it lives on the two screens with nothing loaded
 *
 * Sign-in and "cannot reach the daemon" are the only places it can be useful.
 * Once there is a session the address is demonstrably right, and a field that
 * only ever confirms something is a field that only ever invites a typo. The
 * unreachable screen is the important one: on a phone the built-in default is
 * `localhost`, which is the phone itself, so **the first thing a new install
 * shows is an error that this field is the fix for**. Making somebody find a
 * settings page to recover from a screen that has no settings page would be a
 * dead end.
 *
 * Collapsed behind a summary because it is the rare case. Somebody signing in
 * on a machine that already works should see a username and a password, and
 * nothing that implies a decision is owed.
 */

import { useState } from "preact/hooks";
import { daemonAddress } from "@transport";

export function ServerAddress({ onChanged }: { onChanged?: () => void }) {
  // Captured once: `daemonAddress` is a module constant and the null check has
  // to survive into the callbacks below.
  const address = daemonAddress;
  const [value, setValue] = useState(address?.get() ?? "");
  const [saved, setSaved] = useState(false);
  if (!address) return null;

  const apply = (next: string) => {
    address.set(next);
    // Read back rather than trusting the input: an empty field means "use the
    // built-in", and the person should see which address that turned out to be
    // rather than an empty box that appears to have lost their setting.
    setValue(address.get());
    setSaved(true);
    onChanged?.();
  };

  return (
    <details class="server-address">
      <summary>Server address</summary>
      <div class="field">
        <label for="daemon">Where HydraMail is running</label>
        <input
          id="daemon"
          name="server"
          type="url"
          inputMode="url"
          autocomplete="url"
          spellcheck={false}
          placeholder={address.fallback}
          value={value}
          onInput={(e) => {
            setValue((e.target as HTMLInputElement).value);
            setSaved(false);
          }}
          // Applied on blur and on Enter rather than on every keystroke: this
          // is read on the way into every request, and re-pointing the client
          // at a different host halfway through typing one would fire a request
          // at whatever prefix had been typed so far.
          onBlur={(e) => apply((e.target as HTMLInputElement).value)}
          onKeyDown={(e) => {
            if (e.key !== "Enter") return;
            // The field sits inside the sign-in form, and Enter there means
            // "submit". It cannot mean that while somebody is correcting the
            // address, because submitting would send a password to the host
            // they were part-way through replacing.
            e.preventDefault();
            apply((e.target as HTMLInputElement).value);
          }}
        />
        <p class="note">
          {saved ? "Saved. Try again." : `Leave empty to use ${address.fallback}.`}
        </p>
      </div>
    </details>
  );
}
